import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, FilterIcon } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import EmptyState from '../components/EmptyState';
import { GroupCard } from '../components/GroupCard';
import { CreateGroupModal } from '../components/CreateGroupModal';
// Mock Data for Groups
const MOCK_GROUPS = [{
  id: '1',
  name: 'Remedial Math Group A',
  description: 'Extra support for algebra concepts',
  learnerCount: 8,
  createdBy: 'Sarah Johnson',
  createdOn: 'Mar 15, 2024',
  status: 'active' as const
}, {
  id: '2',
  name: 'Advanced Reading Circle',
  description: 'Accelerated reading program',
  learnerCount: 12,
  createdBy: 'Michael Chen',
  createdOn: 'Mar 10, 2024',
  status: 'active' as const
}, {
  id: '3',
  name: 'Science Practice Group',
  description: 'Weekly lab practice sessions',
  learnerCount: 15,
  createdBy: 'Sarah Johnson',
  createdOn: 'Feb 28, 2024',
  status: 'active' as const
}];
export function Groups() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [groups, setGroups] = useState(MOCK_GROUPS);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('active');
  const filteredGroups = groups.filter(g => {
    const matchesSearch = g.name.toLowerCase().includes(searchQuery.toLowerCase()) || g.description && g.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && g.status === 'active' || statusFilter === 'archived' && g.status === 'archived';
    return matchesSearch && matchesStatus;
  });
  const handleCreateGroup = async (data: any) => {
    console.log('Creating group:', data);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    const newGroup = {
      id: String(groups.length + 1),
      name: data.name,
      description: data.description || '',
      learnerCount: 0,
      createdBy: 'Current User',
      createdOn: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      status: 'active' as const
    };
    setGroups([...groups, newGroup]);
    setIsCreateModalOpen(false);
  };
  return <div className="flex flex-col min-h-screen bg-background" data-id="element-3048">
      <Header data-id="element-3049" />
      <div className="flex flex-1 overflow-hidden" data-id="element-3050">
        <div className="hidden md:block" data-id="element-3051">
          <Sidebar data-id="element-3052" />
        </div>
        <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-3053">
          <main className="flex-1" data-id="element-3054">
            <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-3055">
              {/* Header Section */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-3056">
                <div data-id="element-3057">
                  <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-3058">
                    Groups
                  </h1>
                  <p className="text-text-secondary" data-id="element-3059">
                    Flexible groups for remedial batches, practice sets, and
                    short-term cohorts
                  </p>
                </div>

                <button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl font-semibold hover:bg-[#00a055] transition-all shadow-sm" data-id="element-3060">
                  <PlusIcon size={20} data-id="element-3061" />
                  Create Group
                </button>
              </div>

              {/* Search & Filters */}
              <div className="flex items-center gap-3 mb-6" data-id="element-3062">
                <div className="relative flex-1 max-w-md" data-id="element-3063">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" data-id="element-3064" />
                  <input type="text" placeholder="Search groups..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F]" data-id="element-3065" />
                </div>

                <div className="relative" data-id="element-3066">
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2.5 bg-white border border-custom rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 appearance-none cursor-pointer pr-10" data-id="element-3067">
                    <option value="active" data-id="element-3068">Active</option>
                    <option value="archived" data-id="element-3069">Archived</option>
                    <option value="all" data-id="element-3070">All Status</option>
                  </select>
                </div>

                <button className="p-2.5 bg-white border border-custom rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-50 transition-colors" data-id="element-3071">
                  <FilterIcon size={18} data-id="element-3072" />
                </button>
              </div>

              {/* Groups Grid */}
              {filteredGroups.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3073">
                  {filteredGroups.map(group => <GroupCard key={group.id} {...group} data-id="element-3074" />)}
                </div> : <EmptyState icon={<div size={48} className="text-text-tertiary" data-id="element-3076" />} title="No groups found" description={searchQuery ? 'Try adjusting your search or filters.' : 'Create flexible groups for remedial batches, practice sets, and short-term cohorts.'} actionLabel={!searchQuery ? 'Create Group' : undefined} onAction={!searchQuery ? () => setIsCreateModalOpen(true) : undefined} data-id="element-3075" />}
            </div>
          </main>
          <Footer data-id="element-3077" />
        </div>
      </div>

      <CreateGroupModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onCreate={handleCreateGroup} data-id="element-3078" />
    </div>;
}